import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { getScoreColor, getScoreLabel, channelIcons } from '../data/mockData';
import SafeIcon from '../common/SafeIcon';
import SortableTableHeader from './SortableTableHeader';
import * as FiIcons from 'react-icons/fi';

const { FiPlay, FiImage, FiEye } = FiIcons;

const EnhancedMediaTable = ({ mediaData }) => {
  const navigate = useNavigate();
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [searchFilters, setSearchFilters] = useState({});

  const handleViewDetails = (mediaId) => {
    console.log('Table: Navigating to media detail:', mediaId); // Debug log
    navigate(`/media/${mediaId}`);
  };

  const calculateOverallScore = (channels) => {
    const scores = Object.values(channels).map(channel => channel.score);
    return Math.round(scores.reduce((sum, score) => sum + score, 0) / scores.length);
  };

  const calculateTotalSpent = (channels) => {
    return Object.values(channels).reduce((sum, channel) => sum + channel.spent, 0);
  };

  const calculateTotalConversions = (channels) => {
    return Object.values(channels).reduce((sum, channel) => sum + channel.conversions, 0);
  };

  const calculateAverageRoas = (channels) => {
    const roas = Object.values(channels).map(channel => channel.roas);
    return (roas.reduce((sum, r) => sum + r, 0) / roas.length).toFixed(1);
  };

  const columns = [
    { key: 'thumbnail', label: 'รูปภาพ', sortable: false, searchable: false },
    { key: 'name', label: 'ชื่อสื่อ', sortable: true, searchable: true },
    { key: 'product', label: 'สินค้า', sortable: true, searchable: true },
    { key: 'overallScore', label: 'คะแนนรวม', sortable: true, searchable: false },
    { key: 'channels', label: 'ช่องทางที่ใช้', sortable: false, searchable: false },
    { key: 'averageRoas', label: 'ROAS เฉลี่ย', sortable: true, searchable: false },
    { key: 'totalSpent', label: 'งบประมาณรวม', sortable: true, searchable: false },
    { key: 'totalConversions', label: 'Conversions รวม', sortable: true, searchable: false },
    { key: 'actions', label: 'การดำเนินการ', sortable: false, searchable: false }
  ];

  const processedData = useMemo(() => {
    return mediaData.map(media => ({
      ...media,
      overallScore: calculateOverallScore(media.channels),
      totalSpent: calculateTotalSpent(media.channels),
      totalConversions: calculateTotalConversions(media.channels),
      averageRoas: parseFloat(calculateAverageRoas(media.channels))
    }));
  }, [mediaData]);

  const filteredAndSortedData = useMemo(() => {
    let filtered = processedData;

    // Apply search filters
    Object.entries(searchFilters).forEach(([key, value]) => {
      if (value) {
        filtered = filtered.filter(item => {
          const fieldValue = item[key]?.toString().toLowerCase() || '';
          return fieldValue.includes(value.toLowerCase());
        });
      }
    });

    // Apply sorting
    if (sortConfig.key) {
      filtered.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        // Handle numeric values
        if (typeof aValue === 'number' && typeof bValue === 'number') {
          return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
        }

        // Handle string values
        aValue = aValue?.toString().toLowerCase() || '';
        bValue = bValue?.toString().toLowerCase() || '';

        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }

    return filtered;
  }, [processedData, searchFilters, sortConfig]);

  const handleSort = (key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleSearchChange = (key, value) => {
    setSearchFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleRowClick = (mediaId) => {
    console.log('Row clicked, navigating to:', mediaId); // Debug log
    handleViewDetails(mediaId);
  };

  return (
    <motion.div
      className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div className="p-6 border-b border-gray-200">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">สื่อโฆษณาทั้งหมด</h2>
            <p className="text-sm text-gray-500 mt-1">
              แสดง {filteredAndSortedData.length} จาก {mediaData.length} รายการ
            </p>
          </div>
          {Object.keys(searchFilters).some(key => searchFilters[key]) && (
            <button
              onClick={() => setSearchFilters({})}
              className="text-sm text-blue-600 hover:text-blue-800 font-medium"
            >
              ล้างตัวกรอง
            </button>
          )}
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <SortableTableHeader
            columns={columns}
            sortConfig={sortConfig}
            onSort={handleSort}
            searchFilters={searchFilters}
            onSearchChange={handleSearchChange}
          />
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredAndSortedData.map((media, index) => (
              <motion.tr
                key={media.id}
                className="hover:bg-gray-50 transition-colors cursor-pointer"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                onClick={() => handleRowClick(media.id)}
              >
                {/* รูปภาพสื่อโฆษณา */}
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="relative">
                    <img 
                      src={media.thumbnail} 
                      alt={media.name} 
                      className="w-20 h-20 rounded-lg object-cover shadow-sm"
                    />
                    <div className="absolute -top-1 -right-1 bg-white rounded-full p-1 shadow-sm">
                      <SafeIcon icon={media.type === 'video' ? FiPlay : FiImage} className="h-3 w-3 text-gray-600" />
                    </div>
                    <div className="absolute bottom-1 left-1">
                      <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                        media.type === 'video' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'
                      }`}>
                        {media.type === 'video' ? 'วิดีโอ' : 'รูปภาพ'}
                      </span>
                    </div>
                  </div>
                </td>

                {/* ชื่อสื่อ */}
                <td className="px-6 py-4">
                  <div className="text-sm font-medium text-gray-900 leading-5">
                    {media.name}
                  </div>
                </td>

                {/* สินค้า */}
                <td className="px-6 py-4">
                  <div className="text-sm text-gray-900">{media.product}</div>
                </td>

                {/* คะแนนรวม */}
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center space-x-2">
                    <span className={`text-sm font-medium px-3 py-1 rounded-full ${getScoreColor(media.overallScore)}`}>
                      {media.overallScore}
                    </span>
                    <span className="text-xs text-gray-500">({getScoreLabel(media.overallScore)})</span>
                  </div>
                </td>

                {/* ช่องทางที่ใช้ */}
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex flex-wrap items-center gap-2">
                    {Object.entries(media.channels).map(([channelId, channel]) => (
                      <div key={channelId} className="flex items-center space-x-1">
                        <span className="text-lg" title={channel.name}>{channelIcons[channelId]}</span>
                        <span className={`text-xs font-medium px-2 py-1 rounded-full ${getScoreColor(channel.score)}`}>
                          {channel.score}
                        </span>
                      </div>
                    ))}
                  </div>
                </td>

                {/* ROAS เฉลี่ย */}
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">{media.averageRoas}x</div>
                </td>

                {/* งบประมาณรวม */}
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">฿{media.totalSpent.toLocaleString()}</div>
                </td>

                {/* Conversions รวม */}
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">{media.totalConversions}</div>
                </td>

                {/* การดำเนินการ */}
                <td className="px-6 py-4 whitespace-nowrap">
                  <motion.button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleViewDetails(media.id);
                    }}
                    className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-blue-700 transition-colors"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <SafeIcon icon={FiEye} className="h-4 w-4" />
                    <span>ดูรายละเอียด</span>
                  </motion.button>
                </td>
              </motion.tr>
            ))}
          </tbody>
        </table>

        {filteredAndSortedData.length === 0 && (
          <div className="text-center py-12">
            <div className="text-gray-500">ไม่พบข้อมูลที่ตรงกับการค้นหา</div>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default EnhancedMediaTable;